<template>
  <div v-if="isExchangeFeeOverridable">
    <transition name="squish fade">
      <div
        v-if="isMounted"
        class="form-section group">
        <span class="icon"><icon-colored-fees /></span>
        {{ capitalize($t('names.exchangeFeeOverride', { n: 1 })) }}
        <div class="filler" />
        <div
          v-if="isToggleEnabled"
          class="toggle">
          {{ capitalize($t('states.enabled')) }}
          <toggle-button
            id="edit_exchange_fee_override_toggle"
            v-model="isExchangeFeeOverrideDisabled"
            :sync="true" />
          {{ capitalize($t('states.disabled')) }}
        </div>
        <div
          v-if="canBeRemoved"
          class="actions">
          <button
            class="btn btn-danger btn-sm"
            @click="$emit('cleared')">
            <icon-delete />
            {{ capitalize($t('actions.remove')) }}
          </button>
        </div>
      </div>
    </transition>
    <transition name="squish fade">
      <div
        v-if="!isToggleEnabled || !isExchangeFeeOverrideDisabled"
        class="form-section-content">
        <div class="form-group row">
          <form-label :is-required="true">{{ capitalize($t('names.type', { n: 1 })) }}</form-label>
          <div class="input-group col-sm-8">
            <vue-multiselect
              v-model="overrideType"
              track-by="type"
              label="label"
              :options="overrideTypes"
              :searchable="false"
              :allow-empty="false" />
          </div>
        </div>
        <div class="form-group row">
          <form-label :is-required="true">{{ capitalize($t('names.value', { n: 1 })) }}</form-label>
          <div class="col-sm-8">
            <formatted-input
              v-if="overrideType && overrideType.type === 'FIXED'"
              name="exchangeFeeOverrideValue"
              :is-money="true"
              :value="overrideValue"
              :validation-errors="validationErrors"
              :max="1000"
              :min="0"
              :step="0.01"
              @value-changed="
                (x) => {
                  overrideValue = parseFloat(x);
                }
              " />
            <formatted-input
              v-if="overrideType && overrideType.type === 'PERCENT'"
              name="exchangeFeeOverrideValue"
              :is-money="false"
              :value="overrideValue"
              :validation-errors="validationErrors"
              :max="99"
              :min="0"
              :step="0.1"
              suffix="%"
              @value-changed="
                (x) => {
                  overrideValue = parseFloat(x);
                }
              " />
            <span
              v-show="$validator.messageBag.errors.has('exchangeFeeOverrideValue')"
              class="help is-danger"
              >{{ $validator.messageBag.errors.first('exchangeFeeOverrideValue') }}</span
            >
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import numbro from 'numbro';
  import { capitalize } from 'vue';
  import VueMultiselect from 'vue-multiselect/src/Multiselect';
  import { mapActions, mapGetters, mapState } from 'vuex';

  import { FormattedInput, FormLabel } from '@/common/components/controls';
  import { IconColoredFees } from '@/common/icons/colored';
  import { IconDelete } from '@/common/icons/monochrome';
  import i18n from '@/vueI18n';

  export default {
    name: 'ExchangeFeeOverride',
    components: {
      FormattedInput,
      FormLabel,
      VueMultiselect,
      IconColoredFees,
      IconDelete,
    },
    props: {
      initialOverrideType: {
        type: String,
        default: 'PERCENT',
      },
      initialOverrideValue: {
        type: Number,
        default: null,
      },
      validationErrors: {
        type: Object,
        default: null,
      },
      isToggleEnabled: {
        type: Boolean,
        default: true,
      },
      canBeRemoved: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['valueChanged'],
    data() {
      return {
        isMounted: false,
        isExchangeFeeOverrideDisabled: true,
        overrideType: {
          type: 'PERCENT',
          label: capitalize(i18n.global.t('states.percent')),
        },
        overrideTypes: [
          {
            type: 'PERCENT',
            label: capitalize(i18n.global.t('states.percent')),
          },
          {
            type: 'FIXED',
            label: capitalize(i18n.global.t('states.fixed')),
          },
        ],
        overrideValue: 0,
      };
    },
    computed: {
      ...mapGetters('orgApp', ['isDemandOrganization', 'isSupplyOrganization']),
      ...mapState('orgApp', {
        organizationSettings: (state) => state.organizationSettings,
      }),
      currencySymbol() {
        return numbro.languageData().currency.symbol;
      },
      isExchangeFeeOverridable() {
        return (
          this.isDemandOrganization &&
          (this.organizationSettings === null ||
            (this.organizationSettings !== null && this.organizationSettings.allowExchangeFeeOverride))
        );
      },
    },
    watch: {
      isExchangeFeeOverrideDisabled() {
        this.emitChange();
      },
      overrideType() {
        this.emitChange();
      },
      overrideValue() {
        this.emitChange();
      },
    },
    mounted() {
      this.isMounted = true;
      if (this.initialOverrideType === 'PERCENT') {
        this.isExchangeFeeOverrideDisabled = false;
        this.overrideType = {
          type: 'PERCENT',
          label: capitalize(i18n.global.t('states.percent')),
        };
        this.overrideValue = parseFloat(this.initialOverrideValue) * 100 || 0;
      } else if (this.initialOverrideType === 'FIXED') {
        this.isExchangeFeeOverrideDisabled = false;
        this.overrideType = {
          type: 'FIXED',
          label: capitalize(i18n.global.t('states.fixed')),
        };
        this.overrideValue = this.initialOverrideValue || 0;
      }
    },
    beforeMount() {
      return this.getOrganizationSettings().then(() => this.emitChange());
    },
    unmounted() {
      this.isMounted = false;
    },
    methods: {
      capitalize,
      ...mapActions('orgApp', ['getOrganizationSettings']),
      emitChange() {
        if (this.isExchangeFeeOverrideDisabled) {
          this.$emit('valueChanged', {
            value: null,
            type: null,
          });
          return;
        }
        this.$emit('valueChanged', {
          value: this.overrideType.type === 'PERCENT' ? this.overrideValue / 100 : this.overrideValue,
          type: this.overrideType.type,
        });
      },
    },
  };
</script>

<style scoped></style>
